import type { ReactNode } from 'react'

import { cx } from '../lib/cx'

/**
 * Grilla de dos columnas que colapsa a una sola abajo de 1080px. Las tres
 * proporciones cubren los tres casos reales del panel y no hay una cuarta:
 * cuando una pantalla necesita otra cosa, lo que falta es una decisión de
 * diseño, no un valor de `grid-template-columns`.
 */
export interface TwoColProps {
  /** Columna principal. */
  children: ReactNode
  /** Columna secundaria. Cuando falta, la principal ocupa todo el ancho. */
  aside?: ReactNode
  /**
   * `igual` mitad y mitad · `principal` 1,15fr / 1fr · `rail` contenido + riel
   * fijo de 312px (el riel de seguimientos).
   */
  ratio?: 'igual' | 'principal' | 'rail'
  gap?: 'sm' | 'md' | 'lg'
  /** El aside queda pegado al scroll. Se desactiva solo al colapsar. */
  asideSticky?: boolean
  className?: string
}

const RATIO: Record<NonNullable<TwoColProps['ratio']>, string> = {
  igual: 'lg:grid-cols-2',
  principal: 'lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]',
  rail: 'lg:grid-cols-[minmax(0,1fr)_312px]',
}

const GAP: Record<NonNullable<TwoColProps['gap']>, string> = {
  sm: 'gap-3',
  md: 'gap-5',
  lg: 'gap-6',
}

export function TwoCol({
  children,
  aside,
  ratio = 'principal',
  gap = 'md',
  asideSticky,
  className,
}: TwoColProps) {
  return (
    <div
      data-testid="two-col"
      data-ratio={ratio}
      className={cx(
        'grid min-w-0 grid-cols-1 items-start',
        aside !== undefined && RATIO[ratio],
        GAP[gap],
        className,
      )}
    >
      <div className="min-w-0">{children}</div>
      {aside !== undefined ? (
        <div
          className={cx('min-w-0', asideSticky === true && 'lg:sticky lg:top-4')}
        >
          {aside}
        </div>
      ) : null}
    </div>
  )
}
